import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useAuth0 } from '@auth0/auth0-react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';

import Spinner from 'src/components/library/spinner/Spinner';
import { sessionAccessSelector } from 'src/routes/auth/auth.selectors';
import { receiveAuth0Success } from 'src/routes/auth/auth.actions';
import { setUserPersonalInformation } from 'src/store/user/user.actions';
import { getUserPersonalInformation } from 'src/store/user/user.operations';

const LoadingShell = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100vw;
    height: 100vh;
    background-color: ${({ theme }) => theme?.colors?.background?.default || '#ffffff'};
`;

/**
 * Waits for Auth0 to resolve and silently bootstraps Redux session state
 * without changing the browser URL. Prevents dead-link hard reloads from being
 * redirected to /login before the authenticated router can render a 404.
 */
const AppAuthGate = ({ children }) => {
    const dispatch = useDispatch();
    const access = useSelector(sessionAccessSelector);
    const bootstrapStartedRef = useRef(false);
    const {
        isLoading,
        isAuthenticated,
        user,
        getAccessTokenSilently,
    } = useAuth0();

    useEffect(() => {
        if (isLoading || !isAuthenticated || access || bootstrapStartedRef.current) {
            return undefined;
        }

        bootstrapStartedRef.current = true;
        let isActive = true;

        if (user) {
            dispatch(setUserPersonalInformation({
                email: user.email,
                picture: user.picture,
            }));
        }

        const bootstrapSession = async () => {
            try {
                const token = await getAccessTokenSilently();

                if (!isActive) {
                    return;
                }

                dispatch(receiveAuth0Success({ token }));
                await dispatch(getUserPersonalInformation());
            } catch (error) {
                console.error('Failed to bootstrap Auth0 session:', error);
                bootstrapStartedRef.current = false;
            }
        };

        bootstrapSession();

        return () => {
            isActive = false;
        };
    }, [
        access,
        dispatch,
        getAccessTokenSilently,
        isAuthenticated,
        isLoading,
        user,
    ]);

    if (isLoading || (isAuthenticated && !access)) {
        return (
            <LoadingShell role="status" aria-live="polite" aria-label="Loading authentication">
                <Spinner size="large" />
            </LoadingShell>
        );
    }

    return children;
};

AppAuthGate.propTypes = {
    children: PropTypes.node.isRequired,
};

export default AppAuthGate;
